/**
 * useSpeechRecognition — thin wrapper around the Web Speech API.
 *
 * Supported in Chromium-based browsers (Chrome, Edge, Opera, Brave) and Safari.
 * Firefox does not currently expose SpeechRecognition; `supported` will be false there.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// Minimal ambient typing so we don't depend on dom-speech-recognition lib types.
type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((ev: any) => void) | null;
  onerror:  ((ev: any) => void) | null;
  onend:    (() => void) | null;
  start: () => void;
  stop:  () => void;
  abort: () => void;
};
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface UseSpeechRecognitionOptions {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
  /** Fires for each final transcript chunk. */
  onFinal?: (text: string) => void;
  /** Fires for the most recent interim transcript. */
  onInterim?: (text: string) => void;
}

export interface UseSpeechRecognitionResult {
  supported: boolean;
  listening: boolean;
  interimTranscript: string;
  error: string | null;
  start: () => void;
  stop:  () => void;
  toggle: () => void;
}

export function useSpeechRecognition(opts: UseSpeechRecognitionOptions = {}): UseSpeechRecognitionResult {
  const {
    lang = 'en-US',
    continuous = true,
    interimResults = true,
    onFinal,
    onInterim,
  } = opts;

  const [supported] = useState(() => getCtor() !== null);
  const [listening, setListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recogRef     = useRef<SpeechRecognitionLike | null>(null);
  const onFinalRef   = useRef(onFinal);
  const onInterimRef = useRef(onInterim);
  useEffect(() => { onFinalRef.current   = onFinal;   }, [onFinal]);
  useEffect(() => { onInterimRef.current = onInterim; }, [onInterim]);

  const stop = useCallback(() => {
    recogRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Ctor = getCtor();
    if (!Ctor) {
      setError('Speech recognition is not supported in this browser.');
      return;
    }
    if (recogRef.current) {
      try { recogRef.current.abort(); } catch { /* noop */ }
    }
    const recog = new Ctor();
    recog.lang = lang;
    recog.continuous = continuous;
    recog.interimResults = interimResults;

    recog.onresult = (ev: any) => {
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i];
        const transcript: string = result[0]?.transcript ?? '';
        if (result.isFinal) {
          onFinalRef.current?.(transcript.trim());
        } else {
          interim += transcript;
        }
      }
      setInterimTranscript(interim);
      if (interim) onInterimRef.current?.(interim);
    };
    recog.onerror = (ev: any) => {
      const code = ev?.error ?? 'unknown';
      // Most common: 'no-speech', 'not-allowed', 'audio-capture'
      if (code === 'no-speech') return; // benign silence
      setError(code === 'not-allowed'
        ? 'Microphone permission denied.'
        : `Speech recognition error: ${code}`);
    };
    recog.onend = () => {
      setListening(false);
      setInterimTranscript('');
    };

    try {
      recog.start();
      recogRef.current = recog;
      setError(null);
      setListening(true);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to start microphone.');
    }
  }, [lang, continuous, interimResults]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  useEffect(() => () => {
    try { recogRef.current?.abort(); } catch { /* noop */ }
  }, []);

  return { supported, listening, interimTranscript, error, start, stop, toggle };
}
